import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { errorCodeOf } from "../api/fetcher";
import { Button } from "../components/control/Button";
import { ErrorBanner } from "../components/feedback/ErrorBanner";
import { WorkspaceActions } from "../components/layout/WorkspaceActions";
export interface DraftActionState {
  loaded: boolean;
  dirty: boolean;
  unsynced: boolean;
  saving: boolean;
  applying: boolean;
  error: unknown;
  download?: () => void;
  reload?: () => Promise<unknown>;
  hasRecovery?: boolean;
  retry: () => Promise<unknown>;
  discard: () => Promise<unknown>;
  apply: () => Promise<unknown>;
}
export function DraftActions({
  draft,
  label,
  disabled = false,
  always = false,
  children,
  onApply,
  inline = false,
}: {
  inline?: boolean;
  draft: DraftActionState;
  label?: ReactNode;
  disabled?: boolean;
  always?: boolean;
  children?: ReactNode;
  onApply?: () => void;
}) {
  const { t } = useTranslation("nav");
  const Container = inline ? "div" : WorkspaceActions;
  return (
    <Container>
      <div className="flex w-full flex-wrap items-center justify-end gap-3">
        <div className="min-w-0 flex-1 text-data">
          <p role="status">
            {draft.applying
              ? t("workspace.applying")
              : draft.error
                ? t("workspace.saveError")
                : !draft.loaded
                  ? t("workspace.loading")
                  : draft.saving || draft.unsynced
                    ? t("workspace.saving")
                    : draft.dirty
                      ? t("workspace.saved")
                      : t("workspace.unchanged")}
          </p>
          {children}
          <ErrorBanner error={draft.error} />
          {draft.hasRecovery ? <p>{t("workspace.recoveryHelp")}</p> : null}
        </div>
        {(draft.error || draft.hasRecovery) && draft.download ? (
          <Button variant="secondary" onClick={draft.download}>
            {t("workspace.downloadDraft")}
          </Button>
        ) : null}
        {draft.error && errorCodeOf(draft.error) === "DRAFT_CONFLICT" && draft.reload ? (
          <Button
            variant="secondary"
            disabled={draft.applying}
            onClick={() => void draft.reload?.().catch(() => {})}
          >
            {t("workspace.reloadDraft")}
          </Button>
        ) : null}
        {draft.error ? (
          <Button variant="secondary" onClick={() => void draft.retry().catch(() => {})}>
            {t("workspace.retry")}
          </Button>
        ) : null}
        {draft.dirty ? (
          <Button
            variant="secondary"
            disabled={draft.applying}
            onClick={() => void draft.discard()}
          >
            {t("workspace.discard")}
          </Button>
        ) : null}
        <Button
          busy={draft.applying}
          disabled={disabled || !draft.loaded || (!always && !draft.dirty)}
          onClick={() => (onApply ? onApply() : void draft.apply().catch(() => {}))}
        >
          {label ?? t("workspace.apply")}
        </Button>
      </div>
    </Container>
  );
}
